/* eslint-disable react/default-props-match-prop-types */
/* eslint-disable react/require-default-props */
import React, { memo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCachedTextureUrl } from './textureImageKitService';
// Importiamo i componenti Geist
import { Button, Input, Text, Spacer, Tag, Card, Grid, useTheme } from '@geist-ui/core';
import { XCircle, Filter } from '@geist-ui/icons';

// Configuration constants
export const DEFAULT_SQUARE_SIZE = 200;
export const DEFAULT_BASE_OFFSET = 20;
export const DEFAULT_SKEW_Y = 30;
export const DEFAULT_ANGLE = 15;

// Category base colors for consistent color generation
export const categoryBaseColors = {
  "Metals": 210,
  "Polymers": 120,
  "Ceramics": 30,
  "Wood": 40,
  "Fabrics": 300,
  "Composites": 180,
  "Stone": 0,
  "Organics": 20,
  "Misc.": 270
};

// Utility function to hash a string for color generation
export const hashString = (str) => {
  if (!str) return 0;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

// Generate a color based on texture properties
export const getTextureColor = (texture) => {
  if (!texture) return 'hsl(0, 0%, 50%)';
  
  if (texture.average_color) {
    return texture.average_color;
  }
  
  // Use the category to determine the base hue
  const hue = categoryBaseColors[texture.category_name] || 0;
  
  // Hash the texture name to generate unique saturation and lightness
  const textureHash = hashString(texture.texture_name || "");
  
  // Saturation: 50% to 79% (vary by texture)
  const saturation = 50 + (textureHash % 30);
  
  // Lightness: 40% to 69% (vary by texture)
  const lightness = 40 + (textureHash % 30);
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// Process tags from string or array format
export const processTags = (tagString) => {
  if (!tagString) return [];
  return typeof tagString === 'string' ? tagString.split(' ') : 
         Array.isArray(tagString) ? tagString : [];
};

// Calculate card positions based on filters and scroll state
export const calculateCardPositions = ({
  texturesData,
  activeMaterialFilters,
  activeCategoryFilters,
  searchTerm,
  simulatedScrollY,
  focusedCardIndex,
  hoveredCard,
  selectedTexture,
  selectedIndex,
  stackContainerRef
}) => {
  // Filter textures based on all criteria
  const filteredTextures = texturesData.filter(texture => {
    // Check if any filters are active
    const anyMaterialFilterActive = Object.values(activeMaterialFilters).some(active => active);
    const anyCategoryFilterActive = Object.values(activeCategoryFilters).some(active => active);
    
    // Material filter - if no material filters active, show all. Otherwise, show only matching active filters
    const passesMatFilter = !anyMaterialFilterActive || 
      (texture.material_name && activeMaterialFilters[texture.material_name]);
    
    // Category filter - if no category filters active, show all. Otherwise, show only matching active filters
    const passesCatFilter = !anyCategoryFilterActive || 
      (texture.category_name && activeCategoryFilters[texture.category_name]);
    
    // Search term filter (case insensitive)
    const passesSearch = searchTerm === '' || 
      (texture.texture_name && texture.texture_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (texture.material_name && texture.material_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return passesMatFilter && passesCatFilter && passesSearch;
  });
  
  let selectedTextureInfo = null;
  let selectedTextureIndex = -1;
  
  // Check if the selected texture is still in the filtered list
  if (selectedTexture && selectedIndex !== null) {
    // Find the selected texture in the filtered list
    selectedTextureIndex = filteredTextures.findIndex(t => 
      t.id === selectedTexture.id || 
      (t.texture_name === selectedTexture.texture_name && t.material_name === selectedTexture.material_name)
    );
    
    // If it's not in the filtered list, add it
    if (selectedTextureIndex === -1) {
      // Save selected texture information to maintain its correct z-index
      selectedTextureInfo = {
        texture: selectedTexture,
        originalIndex: texturesData.findIndex(t => 
          t.id === selectedTexture.id || 
          (t.texture_name === selectedTexture.texture_name && t.material_name === selectedTexture.material_name)
        )
      };
      
      // Add the selected texture to the filtered list
      filteredTextures.push(selectedTexture);
    }
  }
  
  const depth = filteredTextures.length;
  const squareSize = DEFAULT_SQUARE_SIZE;
  const baseOffset = DEFAULT_BASE_OFFSET;
  const skewYVal = DEFAULT_SKEW_Y;
  const angle = DEFAULT_ANGLE;
  
  // Calculate effective offset based on simulated scroll
  const effectiveOffset = baseOffset + simulatedScrollY / 20;
  const angleRad = angle * (Math.PI / 180);
  const xOffset = effectiveOffset * Math.cos(angleRad);
  const yOffset = -effectiveOffset * Math.sin(angleRad);
  
  if (!stackContainerRef.current) return [];
  
  const containerWidth = stackContainerRef.current.clientWidth || 800;
  const containerHeight = stackContainerRef.current.clientHeight || 600;

  // Calculate total stack width and height
  const totalWidth = squareSize + (depth - 1) * Math.abs(xOffset);
  const totalHeight = squareSize + (depth - 1) * Math.abs(yOffset);

  // If at minimum spacing, center the stack
  if (simulatedScrollY <= 0) {
    // Minimum spacing, forced centering:
    const centerX = (containerWidth - totalWidth) / 2;
    const centerY = (containerHeight - totalHeight) / 2 + (depth - 1) * Math.abs(yOffset);

    const positions = filteredTextures.map((texture, i) => {
      // Base position and z-index
      const position = {
        x: centerX + i * xOffset,
        y: centerY + i * yOffset,
        skewY: skewYVal
      };
      
      let zIndex = depth - i;
      
      // If this is the selected texture that was added back, use the original z-index
      if (selectedTextureInfo && i === depth - 1 && texture === selectedTexture) {
        // Calculate what the z-index would have been at the original position
        zIndex = depth - selectedTextureInfo.originalIndex;
      }
      
      return {
        texture,
        index: i,
        position,
        zIndex
      };
    });
    
    return positions;
  }

  // If not at minimum spacing, anchor to either the hovered card or focused card
  let startX, startY;

  // Use focused card index or hovered card index (if one exists)
  let anchorIndex = focusedCardIndex;
  
  // If a card is currently hovered, use that as the anchor point instead
  if (hoveredCard.index !== null && hoveredCard.index < depth) {
    anchorIndex = hoveredCard.index;
  }

  if (anchorIndex !== null && anchorIndex < depth) {
    const cards = Array.from(stackContainerRef.current.children);
    const anchorCard = cards[anchorIndex];

    const anchorX = parseFloat(anchorCard?.dataset.originalX ?? 0);
    const anchorY = parseFloat(anchorCard?.dataset.originalY ?? 0);

    startX = anchorX - anchorIndex * xOffset;
    startY = anchorY - anchorIndex * yOffset;
  } else {
    startX = (containerWidth - totalWidth) / 2;
    startY = (containerHeight - totalHeight) / 2 + (depth - 1) * Math.abs(yOffset);
  }

  const positions = filteredTextures.map((texture, i) => {
    // Base position and z-index
    const position = {
      x: startX + i * xOffset,
      y: startY + i * yOffset,
      skewY: skewYVal
    };
    
    let zIndex = depth - i;
    
    // If this is the selected texture that was added back, preserve its original z-index
    if (selectedTextureInfo && i === depth - 1 && texture === selectedTexture) {
      // Calculate what the z-index would have been at the original position
      zIndex = depth - selectedTextureInfo.originalIndex;
    }
    
    return {
      texture,
      index: i,
      position,
      zIndex
    };
  });
  
  return positions;
};

// Create a memoized TextureCard component to prevent unnecessary re-renders
export const TextureCard = memo(({ 
  texture, 
  index, 
  position, 
  depth, 
  zIndex, 
  isAnimating, 
  showTooltip, 
  hideTooltip, 
  updateTooltipPosition,
  hoverClass,
  isSelected,
  onSelect,
  isScrolling
}) => {
  const {x, y, skewY} = position;
  
  // State for the URL of the background image
  const [backgroundUrl, setBackgroundUrl] = useState(null);

  // Use the cached texture if available from our central service
  useEffect(() => {
    if (texture && texture.id) {
      const cachedUrl = getCachedTextureUrl(texture.id);
      setBackgroundUrl(cachedUrl);
    } else {
      setBackgroundUrl(null);
    }
  }, [texture]);
  
  // Compute class names for the card
  const classNames = [
    'square',
    hoverClass ? hoverClass : '',
    isSelected ? 'selected' : '',
    isScrolling ? 'scrolling' : ''
  ].filter(Boolean).join(' ');
  
  // Handle events without triggering unnecessary state updates
  const onMouseEnter = () => {
    if (isAnimating) return;
    showTooltip(index, texture);
  };
  
  const onMouseMove = (e) => {
    if (isAnimating) return;
    updateTooltipPosition(e.clientX, e.clientY);
    e.stopPropagation();
  };
  
  const onMouseLeave = () => {
    if (isAnimating) return;
    hideTooltip();
  };
  
  const handleClick = (e) => {
    e.stopPropagation();
    if (isAnimating) return;
    onSelect(texture, index);
  };

  return (
    <div 
      className={classNames}
      style={{
        '--tx': `${x}px`,
        '--ty': `${y}px`,
        '--skewY': `${skewY}deg`,
        // If we have a backgroundUrl, use the image with cover;
        // otherwise use the fallback color.
        backgroundColor: backgroundUrl ? 'transparent' : getTextureColor(texture),
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        zIndex: zIndex,
        width: `${DEFAULT_SQUARE_SIZE}px`,
        height: `${DEFAULT_SQUARE_SIZE}px`,
        cursor: 'pointer',
        border: 'none',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.4)'
      }}
      data-index={index}
      data-original-x={x}
      data-original-y={y}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
    />
  );
});

// Tooltip component to replace manual DOM creation
export const Tooltip = ({ visible, position, content }) => {
  const theme = useTheme();
  const isDark = theme.type === 'customDark';
  
  // Costante per il bordo arrotondato, usato per mantenere consistenza
  const BORDER_RADIUS = '20px';
  
  // Effetto glow per la dark mode, ombra per la light mode
  const shadowStyle = isDark 
    ? '0 0 15px rgba(100, 100, 255, 0.25), 0 0 8px rgba(150, 150, 255, 0.15)' 
    : '0 8px 16px rgba(0, 0, 0, 0.1)';
  
  return (
    <div 
      style={{
        position: 'fixed',
        left: `${position.x + 3}px`,
        top: `${position.y + 3}px`,
        zIndex: 1000,
        pointerEvents: 'none',
        backgroundColor: isDark ? '#333' : 'rgba(255, 255, 255, 0.95)',
        color: isDark ? '#eaeaea' : '#333',
        padding: '10px 14px',
        borderRadius: BORDER_RADIUS,
        fontSize: '14px',
        maxWidth: '250px',
        boxShadow: shadowStyle,
        border: isDark ? '1px solid #444' : '1px solid #eaeaea',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.2s ease-out',
        visibility: visible ? 'visible' : 'hidden',
        transitionProperty: 'opacity, visibility',
        transitionDelay: visible ? '0s' : '0s, 0.2s'
      }}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};

// Detail card component for showing texture details
export const DetailCard = ({ texture, visible, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.type === 'customDark';
  
  // Early return after hooks declaration to avoid React Hook rule violation
  if (!texture) return null;
  
  const tactileTags = processTags(texture.tactile_tags);
  const aestheticTags = processTags(texture.aesthetic_tags);
  const technicalTags = processTags(texture.technical_tags);
  
  // Funzioni per le azioni sui pulsanti
  const handleGoToTechnicalSheet = () => {
    navigate('/page2', { state: { texture } });
  };
  
  const handleGoToDiscovery = () => {
    navigate('/page3', { state: { texture } });
  };

  // Costante per il bordo arrotondato, usato per mantenere consistenza
  const BORDER_RADIUS = '20px';
  
  // Effetto glow per la dark mode, ombra per la light mode
  const shadowStyle = isDark 
    ? '0 0 15px rgba(100, 100, 255, 0.25), 0 0 8px rgba(150, 150, 255, 0.15)' 
    : '0 8px 16px rgba(0, 0, 0, 0.1)';

  return (
    <div className={`texture-detail-panel ${visible ? 'visible' : ''}`} style={{ 
      position: 'fixed',
      right: '20px', 
      bottom: '20px',
      maxWidth: '350px',
      width: '30%',
      zIndex: 2000,
      transition: 'all 0.3s ease',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(20px)',
      pointerEvents: visible ? 'all' : 'none',
      borderRadius: BORDER_RADIUS,
      overflow: 'hidden'
    }}>
      <Card shadow={false} width="100%" style={{ 
        borderRadius: BORDER_RADIUS, 
        overflow: 'hidden',
        background: isDark ? '#333' : undefined,
        border: isDark ? '1px solid #444' : undefined,
        boxShadow: shadowStyle
      }}>
        <Card.Content style={{ 
          padding: '20px',
          paddingBottom: '0',  /* Rimuovo il padding inferiore */
          background: isDark ? '#333' : undefined,
          color: isDark ? '#eaeaea' : undefined
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: '10px'
          }}>
            <Text h4 my={0}>{texture.texture_name}</Text>
            <div 
              onClick={onClose}
              style={{
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                borderRadius: '50%'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = 0.7}
              onMouseOut={(e) => e.currentTarget.style.opacity = 1}
            >
              <XCircle size={20} />
            </div>
          </div>

          <div style={{ 
            height: '4px', 
            backgroundColor: texture.average_color || getTextureColor(texture), 
            borderRadius: '8px',
            marginBottom: '15px'
          }} />

          {/* Prima riga con materiale e categoria */}
          <div style={{ 
            display: 'flex', 
            gap: '15px',
            marginBottom: '15px'
          }}>
            <div style={{ flex: 1 }}>
              <Text small>{texture.material_name}</Text>
            </div>
            <div style={{ flex: 1 }}>
              <Text small>{texture.category_name}</Text>
            </div>
          </div>

          {/* Descrizione */}
          {texture.description && (
            <div style={{ marginBottom: '15px' }}>
              <Text small>{texture.description}</Text>
            </div>
          )}

          <Spacer h={0.5} />

          <div>
            <Text small b>Tag Tattili</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
              {tactileTags.map((tag, idx) => (
                <Tag 
                  key={`tactile-${idx}`} 
                  type="lite"
                  style={{ 
                    borderRadius: BORDER_RADIUS,
                    backgroundColor: isDark ? '#3a3a3a' : '#f5f5f5',
                    color: isDark ? '#999' : '#666'
                  }}
                >
                  {tag}
                </Tag>
              ))}
              {tactileTags.length === 0 && <Text small i>Nessun tag</Text>}
            </div>
          </div>

          <Spacer h={0.5} />

          <div>
            <Text small b>Tag Estetici</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
              {aestheticTags.map((tag, idx) => (
                <Tag 
                  key={`aesthetic-${idx}`} 
                  type="lite"
                  style={{ 
                    borderRadius: BORDER_RADIUS,
                    backgroundColor: isDark ? '#3a3a3a' : '#f5f5f5',
                    color: isDark ? '#999' : '#666'
                  }}
                >
                  {tag}
                </Tag>
              ))}
              {aestheticTags.length === 0 && <Text small i>Nessun tag</Text>}
            </div>
          </div>

          <Spacer h={0.5} />

          <div>
            <Text small b>Tag Tecnici</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
              {technicalTags.map((tag, idx) => (
                <Tag 
                  key={`technical-${idx}`} 
                  type="lite"
                  style={{ 
                    borderRadius: BORDER_RADIUS,
                    backgroundColor: isDark ? '#3a3a3a' : '#f5f5f5',
                    color: isDark ? '#999' : '#666'
                  }}
                >
                  {tag}
                </Tag>
              ))}
              {technicalTags.length === 0 && <Text small i>Nessun tag</Text>}
            </div>
          </div>
          
          <Spacer h={1} />
          
          {/* Bottoni spostati dal footer al content */}
          <div style={{ 
            display: 'flex', 
            width: '100%',
            gap: '8px',
            boxSizing: 'border-box',
            marginTop: '10px',
            borderTop: '1px solid #eaeaea',
            padding: '15px 0 15px 0'
          }}>
            <Button 
              type="secondary"
              style={{ 
                borderRadius: BORDER_RADIUS,
                flex: 1,
                height: '40px',
                fontSize: '14px',
                margin: 0,
                boxSizing: 'border-box',
                minWidth: '45%',
                backgroundColor: isDark ? '#444' : undefined,
                border: isDark ? '1px solid #555' : undefined,
                color: isDark ? '#fff' : undefined,
                transition: 'all 0.2s ease, box-shadow 0.3s ease'
              }}
              onClick={handleGoToTechnicalSheet}
            >
              Scheda tecnica
            </Button>
            <Button 
              type="secondary"
              style={{ 
                borderRadius: BORDER_RADIUS,
                flex: 1,
                height: '40px',
                fontSize: '14px',
                margin: 0,
                boxSizing: 'border-box',
                minWidth: '45%',
                backgroundColor: isDark ? '#444' : undefined,
                border: isDark ? '1px solid #555' : undefined,
                color: isDark ? '#fff' : undefined,
                transition: 'all 0.2s ease, box-shadow 0.3s ease'
              }}
              onClick={handleGoToDiscovery}
            >
              Discovery
            </Button>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

// Filter panel component
export const FilterPanel = ({ 
  materials = {}, 
  categories = {}, 
  activeMaterialFilters, 
  activeCategoryFilters, 
  onFilterChange, 
  searchTerm, 
  onSearchChange 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();
  const isDark = theme.type === 'customDark';
  
  // Costante per il bordo arrotondato, usato per mantenere consistenza
  const BORDER_RADIUS = '20px';
  const INPUT_BORDER_RADIUS = '28px'; // Bordo più arrotondato per l'input
  
  // Effetto glow per la dark mode, ombra per la light mode
  const shadowStyle = isDark 
    ? '0 0 15px rgba(100, 100, 255, 0.25), 0 0 8px rgba(150, 150, 255, 0.15)' 
    : '0 8px 16px rgba(0, 0, 0, 0.1)';
  
  // Funzione per gestire il toggle con animazione smooth
  const toggleExpand = () => {
    setIsExpanded(prev => !prev);
  };
  
  return (
    <div className="filter-panel" style={{
      position: 'fixed',
      left: '20px',
      bottom: '20px',
      maxWidth: '350px',
      width: isExpanded ? '30%' : '200px',
      zIndex: 1100,
      transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
      borderRadius: BORDER_RADIUS,
      overflow: 'hidden'
    }}>
      <Card shadow={false} width="100%" style={{ 
        borderRadius: BORDER_RADIUS, 
        overflow: 'hidden',
        background: isDark ? '#333' : undefined,
        border: isDark ? '1px solid #444' : undefined,
        boxShadow: shadowStyle
      }}>
        <Card.Content style={{ 
          padding: isExpanded ? '20px' : '12px 15px',
          transition: 'padding 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
          background: isDark ? '#333' : undefined,
          color: isDark ? '#eaeaea' : undefined
        }}>
          <div 
            onClick={toggleExpand} 
            style={{ 
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: isExpanded ? '15px' : 0,
              transition: 'margin-bottom 0.4s cubic-bezier(0.25, 1, 0.5, 1)'
            }}
          >
            <Text h5 my={0} style={{ fontSize: '13px' }}>
              Filtra Textures
            </Text>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Filter size={16} />
            </div>
          </div>
          
          {isExpanded && (
            <div style={{ 
              opacity: isExpanded ? 1 : 0,
              maxHeight: isExpanded ? '1000px' : '0',
              transition: 'opacity 0.4s cubic-bezier(0.25, 1, 0.5, 1), max-height 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
              overflow: 'hidden'
            }}>
              <Grid.Container gap={1}>
                {/* Search input */}
                <Grid xs={24}>
                  <div style={{ width: '100%' }}>
                    <Text small b>Cerca Textures</Text>
                    <Spacer h={0.3} />
                    <Input
                      width="100%"
                      placeholder="Digita per cercare..."
                      value={searchTerm}
                      onChange={(e) => onSearchChange(e.target.value)}
                      scale={0.75}
                      style={{ 
                        borderRadius: INPUT_BORDER_RADIUS,
                        overflow: 'hidden',
                        '& input': { borderRadius: INPUT_BORDER_RADIUS }
                      }}
                      iconRight={<div style={{ paddingRight: '8px' }}></div>}
                    />
                  </div>
                </Grid>
            
                {/* Categories section */}
                <Grid xs={24}>
                  <div style={{ width: '100%' }}>
                    <Text small b>Categorie</Text>
                    <Spacer h={0.3} />
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '4px',
                      maxHeight: '120px',
                      overflowY: 'auto'
                    }}>
                      {categories && Object.keys(categories).map(category => {
                        const isActive = activeCategoryFilters[category];
                        return (
                          <Tag 
                            key={category}
                            type="lite"
                            onClick={() => onFilterChange('category', category)}
                            style={{
                              cursor: 'pointer',
                              borderRadius: BORDER_RADIUS,
                              opacity: isActive ? 1 : 0.85,
                              transition: 'all 0.2s ease',
                              backgroundColor: isActive 
                                ? (isDark ? '#555' : '#eaeaea') 
                                : (isDark ? '#444' : '#f5f5f5'),
                              color: isActive 
                                ? (isDark ? '#fff' : '#333') 
                                : (isDark ? '#eaeaea' : '#666'),
                              fontWeight: isActive ? 'bold' : 'normal',
                              boxShadow: isDark ? '0 2px 5px rgba(100, 100, 255, 0.1)' : 'none'
                            }}
                          >
                            {category}
                          </Tag>
                        );
                      })}
                    </div>
                  </div>
                </Grid>
                
                {/* Materials section */}
                <Grid xs={24}>
                  <div style={{ width: '100%' }}>
                    <Text small b>Materiali</Text>
                    <Spacer h={0.3} />
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '4px',
                      maxHeight: '120px',
                      overflowY: 'auto'
                    }}>
                      {materials && Object.keys(materials).map(material => {
                        const isActive = activeMaterialFilters[material];
                        return (
                          <Tag 
                            key={material}
                            type="default"
                            onClick={() => onFilterChange('material', material)}
                            style={{
                              cursor: 'pointer',
                              borderRadius: BORDER_RADIUS,
                              opacity: isActive ? 1 : 0.6,
                              transition: 'all 0.2s ease',
                              backgroundColor: isActive 
                                ? (isDark ? '#666' : '#333') 
                                : (isDark ? '#444' : '#eaeaea'),
                              color: isActive 
                                ? (isDark ? '#fff' : 'white') 
                                : (isDark ? '#eaeaea' : '#333')
                            }}
                          >
                            {material}
                          </Tag>
                        );
                      })}
                    </div>
                  </div>
                </Grid>
              </Grid.Container>
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

// Helper function to get a card's hover class based on its relationship to hovered card
export const getCardHoverClass = (index, hoveredCard) => {
  if (hoveredCard.index === null) return '';
  if (index === hoveredCard.index) return 'active';
  if (index === hoveredCard.index - 1) return 'adjacent left';
  if (index === hoveredCard.index + 1) return 'adjacent right';
  return '';
};

// Helper function to update container transform
export const updateContainerTransform = (stackContainerRef, panOffset, zoomFactor) => {
  if (stackContainerRef.current) {
    stackContainerRef.current.style.transform = `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomFactor})`;
  }
};

/**
 * Calcola il colore di contrasto ottimale (bianco o nero) per un dato colore di sfondo
 * @param {string} hexColor - Codice colore in formato esadecimale (con o senza #)
 * @returns {string} - Stringa del colore di contrasto ('white' o 'black')
 */
export function getContrastColor(hexColor) {
  // Rimuovi # se presente
  hexColor = hexColor.replace('#', '');
  
  // Parsing dei componenti RGB
  let r = parseInt(hexColor.substring(0, 2), 16) || 0;
  let g = parseInt(hexColor.substring(2, 4), 16) || 0;
  let b = parseInt(hexColor.substring(4, 6), 16) || 0;
  
  // Calcolo della luminanza (formula standard)
  // Valori più alti = colori più chiari, valori più bassi = colori più scuri
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Soglia di luminanza per determinare se usare bianco o nero
  return luminance > 0.5 ? 'black' : 'white';
} 